import React from 'react';
import { SxProps, Theme } from '@mui/material';
import {
  AccessibleOutlined,
  CampaignOutlined,
  ChecklistOutlined,
  DescriptionOutlined,
  GroupsOutlined,
  HandshakeOutlined,
  HubOutlined,
  InfoOutlined,
  LocalLibraryOutlined,
  MailOutlined,
  MedicalInformationOutlined,
  MenuBookOutlined,
  MonitorHeartOutlined,
  NewspaperOutlined,
  GppMaybeOutlined,
  ReportProblemOutlined,
  SchoolOutlined,
  ScienceOutlined,
  SelfImprovementOutlined,
  VisibilityOutlined,
  VolunteerActivismOutlined,
} from '@mui/icons-material';
import { CONTENT_ICON_KEYS } from '../../configuration/contentIcons';

/**
 * Draws the symbol a piece of content names.
 *
 * The key comes out of a content file, so it is text an editor could get
 * wrong - a renamed key, a typo, a symbol removed from the list after it was
 * used. None of that may leave a hole where a symbol was, so an unknown key
 * falls back to the neutral one rather than rendering nothing.
 *
 * The set itself is declared in configuration/contentIcons.json, which is also
 * what the editing interface offers. A key here without an entry there is
 * unreachable, and one there without a component here draws the fallback -
 * both are caught in ContentIcon.test.tsx.
 */
const ICONS: Record<string, React.ElementType> = {
  library: LocalLibraryOutlined,
  guide: MenuBookOutlined,
  document: DescriptionOutlined,
  checklist: ChecklistOutlined,
  medical: MedicalInformationOutlined,
  diagnosis: MonitorHeartOutlined,
  care: VolunteerActivismOutlined,
  severe: AccessibleOutlined,
  pacing: SelfImprovementOutlined,
  warning: ReportProblemOutlined,
  position: GppMaybeOutlined,
  research: ScienceOutlined,
  education: CampaignOutlined,
  training: SchoolOutlined,
  media: NewspaperOutlined,
  letters: MailOutlined,
  cooperation: HandshakeOutlined,
  contacts: HubOutlined,
  community: GroupsOutlined,
  visibility: VisibilityOutlined,
  info: InfoOutlined,
};

/** Exported for the test that holds the two lists against each other. */
export const KNOWN_ICON_KEYS = Object.keys(ICONS);

export const hasContentIcon = (key?: string): boolean =>
  key !== undefined && CONTENT_ICON_KEYS.includes(key);

interface ContentIconProps {
  name?: string;
  /** Font size in pixels; the symbols are drawn on a square. */
  size?: number;
  sx?: SxProps<Theme>;
}

/**
 * Decorative throughout: every symbol sits next to the words it belongs to, so
 * naming it again would only make a screen reader read the heading twice.
 */
export const ContentIcon: React.FC<ContentIconProps> = ({ name, size = 32, sx }) => {
  const Icon = (name === undefined ? undefined : ICONS[name]) ?? InfoOutlined;

  return (
    <Icon aria-hidden="true" sx={{ fontSize: size, color: 'primary.dark', flexShrink: 0, ...sx }} />
  );
};
